import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import {
  createPromoCode,
  deletePromoCode,
  fetchPromoCodeDetail,
  fetchPromoCodes,
  updatePromoCode,
} from "@/lib/promo-codes-api";
import type {
  PromoCodeListParams,
  PromoCodePayload,
} from "@/types/promo-codes";

export function usePromoCodes(params?: PromoCodeListParams) {
  return useQuery({
    queryKey: queryKeys.promoCodes.list(params),
    queryFn: () => fetchPromoCodes(params),
    staleTime: 2 * 60 * 1000,
  });
}

export function usePromoCodeDetail(
  promoCodeId: number | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.promoCodes.detail(promoCodeId ?? 0),
    queryFn: () => fetchPromoCodeDetail(promoCodeId!),
    enabled: enabled && promoCodeId !== null,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreatePromoCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PromoCodePayload) => createPromoCode(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promoCodes.all });
    },
  });
}

export function useUpdatePromoCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      promoCodeId,
      payload,
    }: {
      promoCodeId: number;
      payload: PromoCodePayload;
    }) => updatePromoCode(promoCodeId, payload),
    onSuccess: (_, { promoCodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promoCodes.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.promoCodes.detail(promoCodeId),
      });
    },
  });
}

export function useDeletePromoCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (promoCodeId: number) => deletePromoCode(promoCodeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promoCodes.all });
    },
  });
}
